import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartAddEvent } from '@theme/events';

/**
 * @typedef {object} ApcColorPickerProduct
 * @property {number} id
 * @property {string} handle
 * @property {string} hexcode
 * @property {string} title
 * @property {string} [subtitle]
 * @property {string} textureImage
 * @property {Array<{id:number, title:string, available:boolean, sku?:string}>} variants
 */

/**
 * @typedef {object} ApcColorPickerRefs
 * @property {HTMLElement} currentStep - Wrapper carrying the `data-current-step` attribute.
 * @property {HTMLElement[]} step - The two step panels (`[intro, picker]`).
 * @property {HTMLButtonElement} [takePhoto] - Mobile "take photo" button.
 * @property {HTMLButtonElement[]} uploadFromGallery - "Upload from gallery" buttons.
 * @property {HTMLImageElement} image - The uploaded image to sample.
 * @property {HTMLElement} eyedropper - The draggable eyedropper handle.
 * @property {HTMLElement} eyedropperContainer - The eyedropper + tooltip wrapper.
 * @property {HTMLElement} tooltip - The "drag me" tooltip.
 * @property {HTMLElement} products - The matched-products container.
 * @property {HTMLElement} results - The results panel.
 * @property {HTMLElement} emptyState - The "no match" panel.
 * @property {HTMLElement} addToCartWrapper - The selected-product / add-to-cart bar.
 * @property {HTMLButtonElement} addToCart - Add-to-cart button.
 * @property {HTMLElement} addToCartMessage - "Added to cart" toast.
 * @property {HTMLElement} productTitle - Selected product title node.
 * @property {HTMLElement} productSubtitle - Selected product subtitle node.
 * @property {HTMLElement} [wishlistMount] - Where the wishlist button is injected.
 * @property {HTMLTemplateElement} [wishlistTemplate] - Wishlist button template.
 * @property {HTMLButtonElement} [reset] - Reset button (popup only).
 */

/**
 * Custom colour-collection tool. The customer uploads/takes a photo, drags an
 * eyedropper over it, and the tool finds the closest-matching nail-polish
 * products by perceptual colour distance (CIELAB / deltaE), then adds the
 * matched product to cart (and optionally wishlist).
 *
 * Works both embedded (inline) and inside a `<dialog-component>` popup — the
 * popup handles open/close/scroll-lock; this component only resets its own
 * state when the surrounding dialog closes.
 *
 * @extends {Component<ApcColorPickerRefs>}
 */
class ApcColorPicker extends Component {
  requiredRefs = [
    'currentStep',
    'step',
    'uploadFromGallery',
    'image',
    'eyedropper',
    'eyedropperContainer',
    'tooltip',
    'products',
    'results',
    'emptyState',
    'addToCartWrapper',
    'addToCart',
    'addToCartMessage',
    'productTitle',
    'productSubtitle',
  ];

  /** @type {ApcColorPickerProduct[]} */
  #allProducts = [];
  /** @type {ApcColorPickerProduct | null} */
  #selectedProduct = null;
  /** @type {{id:number} | null} */
  #selectedVariant = null;

  #abortController = new AbortController();
  /** @type {(() => void) | null} */
  #pickerCleanup = null;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #messageTimeout;

  /** @type {HTMLInputElement} */
  #cameraInput;
  /** @type {HTMLInputElement} */
  #galleryInput;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;

    // Hidden file inputs (camera + gallery) drive the upload flow.
    this.#cameraInput = this.#createFileInput(true);
    this.#galleryInput = this.#createFileInput(false);

    // Reset when the surrounding popup closes so the next open starts fresh.
    const dialog = this.closest('dialog-component');
    dialog?.addEventListener('dialog:close', this.handleReset, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#pickerCleanup?.();
    clearTimeout(this.#messageTimeout);
  }

  /* -------------------------------------------------------------------------
   * Step / reset
   * ---------------------------------------------------------------------- */

  /** Resets the tool back to the intro step. */
  handleReset = () => {
    const { step, currentStep, reset, eyedropperContainer } = this.refs;

    step[0]?.setAttribute('data-active', 'true');
    step[1]?.removeAttribute('data-active');
    reset?.removeAttribute('data-active');
    currentStep.setAttribute('data-current-step', '1');

    // Reset eyedropper position to centre.
    if (eyedropperContainer) {
      eyedropperContainer.style.left = '';
      eyedropperContainer.style.top = '';
      eyedropperContainer.style.transform = '';
    }

    this.#pickerCleanup?.();
    this.#pickerCleanup = null;
  };

  /* -------------------------------------------------------------------------
   * File inputs (camera / gallery)
   * ---------------------------------------------------------------------- */

  /** Opens the device camera (declarative `on:click`). */
  openCamera = () => this.#cameraInput.click();

  /** Opens the gallery file picker (declarative `on:click`). */
  openGallery = () => this.#galleryInput.click();

  /**
   * @param {boolean} useCamera
   * @returns {HTMLInputElement}
   */
  #createFileInput(useCamera) {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.value = '';
    if (useCamera) input.capture = 'environment';
    input.style.display = 'none';
    input.addEventListener('change', this.#handleFileSelection, {
      signal: this.#abortController.signal,
    });
    return input;
  }

  /** @param {Event} event */
  #handleFileSelection = (event) => {
    const target = /** @type {HTMLInputElement} */ (event.target);
    const file = target.files?.[0];
    if (!file) return;

    const { image, step, currentStep, reset } = this.refs;

    image.src = URL.createObjectURL(file);

    image.onload = async () => {
      await this.#ensureProductsLoaded();

      this.#initializeColorPicker();

      step[0]?.removeAttribute('data-active');
      step[1]?.setAttribute('data-active', 'true');
      reset?.setAttribute('data-active', 'true');
      currentStep.setAttribute('data-current-step', '2');
    };

    // Reset the input value so picking the same file again still fires `change`.
    target.value = '';
  };

  /** Loads the colour catalogue feed once. */
  async #ensureProductsLoaded() {
    if (this.#allProducts.length) return;

    const searchUrl = this.dataset.searchUrl || Theme.routes.search_url;
    const view = 'apc-color-picker';

    const [page1, page2] = await Promise.all([
      fetch(`${searchUrl}?view=${view}&page=1`).then((res) => res.json()),
      fetch(`${searchUrl}?view=${view}&page=2`).then((res) => res.json()),
    ]);

    this.#allProducts = [...page1, ...page2];
  }

  /* -------------------------------------------------------------------------
   * Colour conversion utilities (ported verbatim)
   * ---------------------------------------------------------------------- */

  /** @param {string} hex */
  #hexToRgb(hex) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16),
        }
      : null;
  }

  /**
   * @param {number} r
   * @param {number} g
   * @param {number} b
   */
  #rgbToLab(r, g, b) {
    const rNorm = r / 255;
    const gNorm = g / 255;
    const bNorm = b / 255;

    const rGamma = rNorm > 0.04045 ? ((rNorm + 0.055) / 1.055) ** 2.4 : rNorm / 12.92;
    const gGamma = gNorm > 0.04045 ? ((gNorm + 0.055) / 1.055) ** 2.4 : gNorm / 12.92;
    const bGamma = bNorm > 0.04045 ? ((bNorm + 0.055) / 1.055) ** 2.4 : bNorm / 12.92;

    let x = (rGamma * 0.4124 + gGamma * 0.3576 + bGamma * 0.1805) / 0.95047;
    let y = (rGamma * 0.2126 + gGamma * 0.7152 + bGamma * 0.0722) / 1.0;
    let z = (rGamma * 0.0193 + gGamma * 0.1192 + bGamma * 0.9505) / 1.08883;

    x = x > 0.008856 ? x ** (1 / 3) : 7.787 * x + 16 / 116;
    y = y > 0.008856 ? y ** (1 / 3) : 7.787 * y + 16 / 116;
    z = z > 0.008856 ? z ** (1 / 3) : 7.787 * z + 16 / 116;

    return { l: 116 * y - 16, a: 500 * (x - y), b: 200 * (y - z) };
  }

  /**
   * @param {{l:number,a:number,b:number}} lab1
   * @param {{l:number,a:number,b:number}} lab2
   */
  #deltaE(lab1, lab2) {
    const deltaL = lab1.l - lab2.l;
    const deltaA = lab1.a - lab2.a;
    const deltaB = lab1.b - lab2.b;
    return Math.sqrt(deltaL * deltaL + deltaA * deltaA + deltaB * deltaB);
  }

  /** @param {string} selectedHex */
  #findClosestProducts(selectedHex) {
    if (!this.#allProducts.length) return [];

    const selectedRgb = this.#hexToRgb(selectedHex);
    if (!selectedRgb) return [];

    const selectedLab = this.#rgbToLab(selectedRgb.r, selectedRgb.g, selectedRgb.b);

    return this.#allProducts
      .map((product) => {
        const productRgb = this.#hexToRgb(product.hexcode);
        if (!productRgb) return { ...product, distance: Infinity };

        const productLab = this.#rgbToLab(productRgb.r, productRgb.g, productRgb.b);
        return { ...product, distance: this.#deltaE(selectedLab, productLab) };
      })
      .filter((product) => product.distance !== Infinity)
      .sort((a, b) => a.distance - b.distance)
      .slice(0, 4);
  }

  /* -------------------------------------------------------------------------
   * Product results
   * ---------------------------------------------------------------------- */

  #loadingStart() {
    this.refs.products.setAttribute('data-loading', 'true');
    this.refs.addToCartWrapper.setAttribute('data-loading', 'true');
  }

  #loadingEnd() {
    this.refs.products.removeAttribute('data-loading');
    this.refs.addToCartWrapper.removeAttribute('data-loading');
  }

  /**
   * @param {ApcColorPickerProduct[]} productResults
   * @param {HTMLButtonElement | null} button
   */
  #handleProductSelect(productResults, button) {
    if (!button) return;

    const { products, productTitle, productSubtitle, addToCartWrapper } = this.refs;

    products.querySelector('button[data-active]')?.removeAttribute('data-active');
    button.setAttribute('data-active', 'true');

    const chosenProduct = productResults.find((product) => product.handle === button.dataset.handle);
    if (!chosenProduct) return;

    // Prefer an available 15 ml variant, else the first variant (legacy parity).
    const validVariant = chosenProduct.variants.find((variant) => variant.available && variant.title.includes('15'));

    this.#selectedProduct = chosenProduct;
    this.#selectedVariant = validVariant || chosenProduct.variants[0];

    productTitle.textContent = chosenProduct.title;
    productSubtitle.textContent = chosenProduct.subtitle || '';
    addToCartWrapper.setAttribute('data-active', 'true');

    this.#renderWishlistButton(chosenProduct.id);
  }

  /** @param {string[]} handles */
  #renderProducts(handles) {
    if (!handles.length) return;

    this.#loadingStart();

    const { products } = this.refs;
    const productResults = this.#allProducts.filter((product) => handles.includes(product.handle));

    products.innerHTML = productResults
      .map(
        (product) => `
          <button
            class="apc-color-picker__swatch"
            type="button"
            data-handle="${product.handle}"
          >
            <img
              class="apc-color-picker__swatch-image"
              style="--apc-hexcode: ${product.hexcode}"
              src="${product.textureImage}"
              alt="${product.handle}"
              height="92"
              width="92"
              loading="eager"
            >
          </button>
        `,
      )
      .join('');

    this.#handleProductSelect(productResults, products.querySelector('button:first-child'));

    products.querySelectorAll('button').forEach((button) => {
      button.addEventListener('click', () => this.#handleProductSelect(productResults, button), {
        signal: this.#abortController.signal,
      });
    });

    this.#loadingEnd();
  }

  /* -------------------------------------------------------------------------
   * Eyedropper + canvas sampling
   * ---------------------------------------------------------------------- */

  #initializeColorPicker() {
    // Tear down any listeners from a previous image before re-initialising.
    this.#pickerCleanup?.();

    const { image, eyedropper, eyedropperContainer, tooltip, results, emptyState } = this.refs;
    const container = image.parentElement;
    if (!container) return;

    const canvas = document.createElement('canvas');
    const canvasCtx = canvas.getContext('2d');
    canvas.style.display = 'none';
    container.appendChild(canvas);

    const updateCanvas = () => {
      canvas.width = image.naturalWidth;
      canvas.height = image.naturalHeight;
      canvasCtx?.drawImage(image, 0, 0);
    };

    const updateColorAtPosition = () => {
      if (!canvasCtx) return null;

      const eyedropperRect = eyedropper.getBoundingClientRect();
      const imgRect = image.getBoundingClientRect();

      const eyedropperCenterX = eyedropperRect.left + eyedropperRect.width / 2;
      const eyedropperCenterY = eyedropperRect.top + eyedropperRect.height / 2;

      const x = eyedropperCenterX - imgRect.left;
      const y = eyedropperCenterY - imgRect.top;

      const imgAspectRatio = image.naturalWidth / image.naturalHeight;
      const containerAspectRatio = imgRect.width / imgRect.height;

      let displayedWidth;
      let displayedHeight;
      let offsetX;
      let offsetY;

      if (imgAspectRatio > containerAspectRatio) {
        displayedWidth = imgRect.width;
        displayedHeight = imgRect.width / imgAspectRatio;
        offsetX = 0;
        offsetY = (imgRect.height - displayedHeight) / 2;
      } else {
        displayedWidth = imgRect.height * imgAspectRatio;
        displayedHeight = imgRect.height;
        offsetX = (imgRect.width - displayedWidth) / 2;
        offsetY = 0;
      }

      const adjustedX = x - offsetX;
      const adjustedY = y - offsetY;

      if (adjustedX < 0 || adjustedX > displayedWidth || adjustedY < 0 || adjustedY > displayedHeight) {
        return null;
      }

      const scaleX = image.naturalWidth / displayedWidth;
      const scaleY = image.naturalHeight / displayedHeight;
      const imgX = Math.floor(adjustedX * scaleX);
      const imgY = Math.floor(adjustedY * scaleY);

      const clampedX = Math.max(0, Math.min(imgX, image.naturalWidth - 1));
      const clampedY = Math.max(0, Math.min(imgY, image.naturalHeight - 1));

      const imageData = canvasCtx.getImageData(clampedX, clampedY, 1, 1);
      const [r, g, b] = imageData.data;
      const hexColor = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b
        .toString(16)
        .padStart(2, '0')}`;

      eyedropper.style.backgroundColor = hexColor;
      const brightness = (r * 299 + g * 587 + b * 114) / 1000;
      eyedropper.style.color = brightness > 128 ? '#000' : '#fff';

      return { hex: hexColor, rgb: `rgb(${r}, ${g}, ${b})`, coordinates: { x: clampedX, y: clampedY } };
    };

    /** Searches and renders products for a sampled colour, toggling empty state. */
    const matchColor = (colorInfo) => {
      if (!colorInfo) {
        results.removeAttribute('data-active');
        emptyState.setAttribute('data-active', 'true');
        return;
      }

      const closestProducts = this.#findClosestProducts(colorInfo.hex);
      if (!closestProducts.length) {
        results.removeAttribute('data-active');
        emptyState.setAttribute('data-active', 'true');
        return;
      }

      results.setAttribute('data-active', 'true');
      emptyState.removeAttribute('data-active');
      this.#renderProducts(closestProducts.map((product) => product.handle));
    };

    // Tooltip — shown until first interaction.
    let hasInteracted = false;
    const hideTooltip = () => {
      if (hasInteracted) return;
      hasInteracted = true;
      tooltip.style.display = 'none';
    };
    const showTooltip = () => {
      tooltip.style.display = 'block';
    };

    // Eyedropper drag (mouse + touch).
    let isDragging = false;
    const dragOffset = { x: 0, y: 0 };

    const beginDrag = (clientX, clientY) => {
      hideTooltip();
      isDragging = true;
      const containerRect = eyedropperContainer.getBoundingClientRect();
      dragOffset.x = clientX - containerRect.left;
      dragOffset.y = clientY - containerRect.top;
    };

    const moveDrag = (clientX, clientY) => {
      if (!isDragging) return;
      const containerRect = container.getBoundingClientRect();
      const newX = clientX - containerRect.left - dragOffset.x;
      const newY = clientY - containerRect.top - dragOffset.y;

      const maxX = container.offsetWidth - eyedropperContainer.offsetWidth;
      const maxY = container.offsetHeight - eyedropperContainer.offsetHeight;

      eyedropperContainer.style.left = `${Math.max(0, Math.min(newX, maxX))}px`;
      eyedropperContainer.style.top = `${Math.max(0, Math.min(newY, maxY))}px`;
      eyedropperContainer.style.transform = 'none';

      updateColorAtPosition();
    };

    const endDrag = () => {
      if (!isDragging) return;
      isDragging = false;
      eyedropper.style.cursor = 'move';
      matchColor(updateColorAtPosition());
    };

    const handleMouseDown = (e) => {
      beginDrag(e.clientX, e.clientY);
      eyedropper.style.cursor = 'grabbing';
      e.preventDefault();
    };
    const handleTouchStart = (e) => {
      const touch = e.touches[0];
      beginDrag(touch.clientX, touch.clientY);
      e.preventDefault();
    };
    const handleMouseMove = (e) => moveDrag(e.clientX, e.clientY);
    const handleTouchMove = (e) => {
      if (!isDragging) return;
      const touch = e.touches[0];
      moveDrag(touch.clientX, touch.clientY);
      e.preventDefault();
    };

    const handleImageClick = (e) => {
      if (isDragging) return;

      const imgRect = image.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();

      const clickX = e.clientX - imgRect.left;
      const clickY = e.clientY - imgRect.top;

      const imgAspectRatio = image.naturalWidth / image.naturalHeight;
      const containerAspectRatio = imgRect.width / imgRect.height;

      let displayedWidth;
      let displayedHeight;
      let offsetX;
      let offsetY;

      if (imgAspectRatio > containerAspectRatio) {
        displayedWidth = imgRect.width;
        displayedHeight = imgRect.width / imgAspectRatio;
        offsetX = 0;
        offsetY = (imgRect.height - displayedHeight) / 2;
      } else {
        displayedWidth = imgRect.height * imgAspectRatio;
        displayedHeight = imgRect.height;
        offsetX = (imgRect.width - displayedWidth) / 2;
        offsetY = 0;
      }

      if (
        clickX < offsetX ||
        clickX > offsetX + displayedWidth ||
        clickY < offsetY ||
        clickY > offsetY + displayedHeight
      ) {
        return;
      }

      const eyedropperX = e.clientX - containerRect.left - eyedropperContainer.offsetWidth / 2;
      const eyedropperY = e.clientY - containerRect.top - eyedropperContainer.offsetHeight / 2;

      const maxX = container.offsetWidth - eyedropperContainer.offsetWidth;
      const maxY = container.offsetHeight - eyedropperContainer.offsetHeight;

      eyedropperContainer.style.left = `${Math.max(0, Math.min(eyedropperX, maxX))}px`;
      eyedropperContainer.style.top = `${Math.max(0, Math.min(eyedropperY, maxY))}px`;
      eyedropperContainer.style.transform = 'none';

      hideTooltip();
      matchColor(updateColorAtPosition());
    };

    eyedropperContainer.addEventListener('mousedown', handleMouseDown);
    eyedropperContainer.addEventListener('touchstart', handleTouchStart, { passive: false });
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('mouseup', endDrag);
    document.addEventListener('touchend', endDrag);
    image.addEventListener('click', handleImageClick);

    updateCanvas();

    // Sample the initial (centre) colour once layout settles.
    setTimeout(() => {
      const initialColor = updateColorAtPosition();
      if (!initialColor) {
        results.removeAttribute('data-active');
        emptyState.setAttribute('data-active', 'true');
        showTooltip();
        return;
      }
      if (this.#allProducts.length > 0) matchColor(initialColor);
      showTooltip();
    }, 50);

    this.#pickerCleanup = () => {
      eyedropperContainer.removeEventListener('mousedown', handleMouseDown);
      eyedropperContainer.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('mouseup', endDrag);
      document.removeEventListener('touchend', endDrag);
      image.removeEventListener('click', handleImageClick);
      canvas.remove();
    };
  }

  /* -------------------------------------------------------------------------
   * Cart
   * ---------------------------------------------------------------------- */

  /** Adds the selected variant to cart (declarative `on:click`). */
  addToCart = async () => {
    const { addToCart, addToCartMessage } = this.refs;
    if (!this.#selectedVariant) return;

    try {
      addToCart.setAttribute('disabled', 'true');

      const formData = new FormData();
      formData.append('id', String(this.#selectedVariant.id));
      formData.append('quantity', '1');

      // Request the cart sections so the cart drawer can morph after adding.
      const sectionIds = [];
      document.querySelectorAll('cart-items-component').forEach((item) => {
        if (item instanceof HTMLElement && item.dataset.sectionId) sectionIds.push(item.dataset.sectionId);
      });
      if (sectionIds.length) formData.append('sections', sectionIds.join(','));

      const fetchCfg = fetchConfig('javascript', { body: formData });
      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchCfg,
        headers: { ...fetchCfg.headers, Accept: 'text/html' },
      }).then((res) => res.json());

      if (response.status) throw new Error(response.message || response.description);

      this.dispatchEvent(
        new CartAddEvent({}, String(this.#selectedVariant.id), {
          source: 'apc-color-picker',
          itemCount: 1,
          productId: this.#selectedProduct ? String(this.#selectedProduct.id) : undefined,
          sections: response.sections,
        }),
      );

      addToCartMessage.setAttribute('data-active', 'true');
      clearTimeout(this.#messageTimeout);
      this.#messageTimeout = setTimeout(() => addToCartMessage.removeAttribute('data-active'), 3000);
    } catch (error) {
      console.error(error);
    } finally {
      addToCart.removeAttribute('disabled');
    }
  };

  /* -------------------------------------------------------------------------
   * Wishlist (reuses the theme's <wishlist-button> custom element)
   * ---------------------------------------------------------------------- */

  /** @param {number} productId */
  #renderWishlistButton(productId) {
    const { wishlistMount, wishlistTemplate } = this.refs;
    if (!wishlistMount || !wishlistTemplate) return;

    // Clone a fresh button per product so its initial state reflects that product.
    const fragment = /** @type {DocumentFragment} */ (wishlistTemplate.content.cloneNode(true));
    const button = fragment.querySelector('wishlist-button');
    if (button instanceof HTMLElement) button.dataset.productId = String(productId);

    wishlistMount.replaceChildren(fragment);
  }
}

if (!customElements.get('apc-color-picker')) {
  customElements.define('apc-color-picker', ApcColorPicker);
}

/**
 * Site-wide popup trigger: any link to `#apc-color-picker` opens the popup
 * (mirrors the legacy `[href*="#color-picker"]` behaviour without editing base files).
 */
function bindColorPickerTriggers() {
  document.querySelectorAll('a[href*="#apc-color-picker"]').forEach((el) => {
    if (el.dataset.apcColorPickerBound) return;
    el.dataset.apcColorPickerBound = 'true';

    el.addEventListener('click', (event) => {
      // Dedicated trigger — never let the `#apc-color-picker` hash land in the URL.
      event.preventDefault();

      const dialog = document.querySelector('dialog-component.apc-color-picker-dialog');
      if (!dialog || typeof dialog.showDialog !== 'function') {
        console.warn('[apc-color-picker] No popup found. Add the Color picker section in popup mode.');
        return;
      }
      dialog.showDialog();
    });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bindColorPickerTriggers);
} else {
  bindColorPickerTriggers();
}
